import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';

/**
 * ContactPage component
 * Displays contact information and a contact form that works without a backend
 * Uses a Google Forms embed styled to match the site design
 */
const ContactPage = () => {
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    game: '',
    message: '',
    service: 'game-translation'
  });
  
  const formRef = useRef(null);
  const isInView = useInView(formRef, { once: true, amount: 0.2 });
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please fill in all required fields.'
      });
      return;
    }
    
    // In a real implementation, this would submit to Google Forms
    // For now, we'll simulate a successful submission
    
    // Create a hidden form that would submit to Google Forms
    const googleForm = document.createElement('form');
    googleForm.action = 'https://docs.google.com/forms/u/0/d/e/YOUR_FORM_ID_HERE/formResponse';
    googleForm.method = 'POST';
    googleForm.target = '_blank';
    
    // Add form data as hidden inputs
    // These entry.XXXXXXX IDs would need to be replaced with actual Google Form field IDs
    const nameInput = document.createElement('input');
    nameInput.type = 'hidden';
    nameInput.name = 'entry.2005620554';
    nameInput.value = formData.name;
    googleForm.appendChild(nameInput);
    
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'entry.1045781291';
    emailInput.value = formData.email;
    googleForm.appendChild(emailInput);
    
    const companyInput = document.createElement('input');
    companyInput.type = 'hidden';
    companyInput.name = 'entry.1166974658';
    companyInput.value = formData.company;
    googleForm.appendChild(companyInput);
    
    const gameInput = document.createElement('input');
    gameInput.type = 'hidden';
    gameInput.name = 'entry.839337160';
    gameInput.value = formData.game;
    googleForm.appendChild(gameInput);
    
    const messageInput = document.createElement('input');
    messageInput.type = 'hidden';
    messageInput.name = 'entry.839337160';
    messageInput.value = formData.message;
    googleForm.appendChild(messageInput);
    
    const serviceInput = document.createElement('input');
    serviceInput.type = 'hidden';
    serviceInput.name = 'entry.1065046570';
    serviceInput.value = formData.service;
    googleForm.appendChild(serviceInput);
    
    // Append form to body, submit it, and remove it
    document.body.appendChild(googleForm);
    googleForm.submit();
    document.body.removeChild(googleForm);
    
    // Show success message
    setFormStatus({
      submitted: true,
      error: false,
      message: 'Thank you for your message! We will get back to you soon.'
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      game: '',
      message: '',
      service: 'game-translation'
    });
  };
  
  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  
  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | NatuXtranslations</title>
        <meta name="description" content="Get in touch with our French game localization team. We're ready to help bring your game to French-speaking players worldwide." />
        <meta name="keywords" content="contact french translator, game localization quote, french translation services" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-32 bg-bg-alt relative">
        <div className="container mx-auto px-6">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-normal mb-8 tracking-wide">
              Get in Touch
            </h1>
            <p className="text-light text-lg md:text-xl font-light leading-relaxed">
              Ready to bring your game to French-speaking players? We're here to help.
              Fill out the form below, and we'll get back to you as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16">
            {/* Contact Form */}
            <motion.div 
              ref={formRef}
              className="w-full lg:w-2/3"
              variants={formVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <div className="border border-primary/10 p-10">
                <h2 className="font-heading text-2xl md:text-3xl font-normal mb-8 tracking-wide">
                  Send Us a Message
                </h2>
                
                {formStatus.submitted ? (
                  <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded">
                    <h3 className="text-xl font-heading mb-3">Message Sent!</h3>
                    <p>{formStatus.message}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {formStatus.error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 rounded">
                        {formStatus.message}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="company" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="game" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                          Game Name
                        </label>
                        <input
                          type="text"
                          id="game"
                          name="game"
                          value={formData.game}
                          onChange={handleChange}
                          className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="service" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                      >
                        <option value="game-translation">Game Translation & Localization</option>
                        <option value="website-localization">Website & Marketing Localization</option>
                        <option value="steam-page">Steam Page Localization</option>
                        <option value="ongoing-support">Ongoing Support & Updates</option>
                        <option value="documentation">Game Documentation</option>
                        <option value="community-management">French Community Management</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="mb-8">
                      <label htmlFor="message" className="block text-sm uppercase tracking-wider text-primary/60 mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="6"
                        className="w-full border border-primary/10 p-3 focus:border-primary focus:outline-none transition-colors duration-300"
                        required
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="bg-primary text-white px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-primary/90 hover:translate-y-[-2px]"
                    >
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
            
            {/* Contact Info */}
            <div className="w-full lg:w-1/3">
              <div className="border border-primary/10 p-10 mb-8">
                <h2 className="font-heading text-2xl font-normal mb-8 tracking-wide">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="text-primary mr-4 mt-1">
                      <FaEnvelope />
                    </div>
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-primary/60 mb-2">Email</h3>
                      <p className="font-light">contact@natuxtranslations.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-primary mr-4 mt-1">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-primary/60 mb-2">Location</h3>
                      <p className="font-light">Based in France, serving clients worldwide</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-primary mr-4 mt-1">
                      <FaClock />
                    </div>
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-primary/60 mb-2">Response Time</h3>
                      <p className="font-light">We typically respond within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border border-primary/10 p-10">
                <h2 className="font-heading text-2xl font-normal mb-6 tracking-wide">
                  What to Expect
                </h2>
                
                <ol className="space-y-4 font-light">
                  <li className="flex">
                    <span className="text-primary mr-3">1.</span>
                    <p>We'll review your inquiry and get back to you within 24 hours.</p>
                  </li>
                  <li className="flex">
                    <span className="text-primary mr-3">2.</span>
                    <p>We'll discuss your project needs and provide a free quote.</p>
                  </li>
                  <li className="flex">
                    <span className="text-primary mr-3">3.</span>
                    <p>Once terms are agreed upon, we'll begin the translation process.</p>
                  </li>
                  <li className="flex">
                    <span className="text-primary mr-3">4.</span>
                    <p>We'll deliver high-quality French translations on schedule.</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-24 bg-bg-alt">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="border border-primary/10 p-8">
                <h3 className="text-xl font-heading mb-4">How much does French game localization cost?</h3>
                <p className="font-light text-light">
                  Pricing depends on several factors including word count, complexity, and timeline. We offer competitive rates and can provide a detailed quote after reviewing your project specifics.
                </p>
              </div>
              
              <div className="border border-primary/10 p-8">
                <h3 className="text-xl font-heading mb-4">How long does the translation process take?</h3>
                <p className="font-light text-light">
                  Timeline varies based on project size and complexity. For a typical indie game, we can complete translation within 2-4 weeks. We'll provide a specific timeline estimate for your project.
                </p>
              </div>
              
              <div className="border border-primary/10 p-8">
                <h3 className="text-xl font-heading mb-4">Do you offer ongoing support after the initial translation?</h3>
                <p className="font-light text-light">
                  Yes! We provide ongoing support for updates, patches, and DLC content. We maintain glossaries and translation memories for your project to ensure consistency across all content.
                </p>
              </div>
              
              <div className="border border-primary/10 p-8">
                <h3 className="text-xl font-heading mb-4">What file formats do you work with?</h3>
                <p className="font-light text-light">
                  We work with most common file formats including .json, .xml, .csv, .xlsx, Unity and Unreal localization files, and more. If you have a specific format, just let us know.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
